import { createBrowserRouter, RouteObject } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { Navigate } from 'react-router-dom';

import { ProtectedRoute } from '@/lib/auth-guard';
import { routes } from '@/lib/routes';
import { AppShell } from '@/components/layout/AppShell';
import { RouteLoader } from '@/components/RouteLoader';
import { AppErrorBoundary } from '@/components/AppErrorBoundary';

// Lazy load route components with proper error handling
const HomeRoute = lazy(() => 
  import('@/features/home/HomeRoute').then(module => ({ default: module.HomeRoute })).catch(err => {
    console.error('Failed to load HomeRoute:', err);
    throw err;
  })
);

const StartPageRoute = lazy(() => 
  import('@/features/initial-onboarding/StartPageRoute').then(module => ({ default: module.StartPageRoute })).catch(err => {
    console.error('Failed to load StartPageRoute:', err);
    throw err;
  })
);

const PersonalityRoute = lazy(() => 
  import('@/features/assessments/PersonalityRoute').then(module => ({ default: module.PersonalityRoute })).catch(err => {
    console.error('Failed to load PersonalityRoute:', err);
    throw err;
  })
);

const InterestsRoute = lazy(() => 
  import('@/features/assessments/InterestsRoute').then(module => ({ default: module.InterestsRoute })).catch(err => {
    console.error('Failed to load InterestsRoute:', err);
    throw err;
  })
);

const TalentsRoute = lazy(() => 
  import('@/features/assessments/TalentsRoute').then(module => ({ default: module.TalentsRoute })).catch(err => {
    console.error('Failed to load TalentsRoute:', err);
    throw err;
  })
);


const PreferencesRoute = lazy(() => 
  import('@/features/preferences/PreferencesRoute').then(module => ({ default: module.PreferencesRoute })).catch(err => {
    console.error('Failed to load PreferencesRoute:', err);
    throw err;
  })
);

const SignInRoute = lazy(() => 
  import('@/features/auth/SignInRoute').catch(err => {
    console.error('Failed to load SignInRoute:', err);
    throw err;
  })
);

const SignUpRoute = lazy(() => 
  import('@/features/auth/SignUpRoute').catch(err => {
    console.error('Failed to load SignUpRoute:', err);
    throw err;
  })
);

const ForgotPasswordRoute = lazy(() => 
  import('@/features/auth/ForgotPasswordRoute').catch(err => {
    console.error('Failed to load ForgotPasswordRoute:', err);
    throw err;
  })
);

const PasswordSetupRoute = lazy(() => 
  import('@/features/auth/PasswordSetupRoute').then(module => ({ default: module.default })).catch(err => {
    console.error('Failed to load PasswordSetupRoute:', err);
    throw err;
  })
);

const Compare = lazy(() => 
  import('@/app/pages/Compare').then(module => ({ default: module.Compare })).catch(err => {
    console.error('Failed to load Compare:', err);
    throw err;
  })
);

const Top3StudiesRoute = lazy(() => 
  import('@/features/top3-studies/Top3StudiesRoute').then(module => ({ default: module.Top3StudiesRoute })).catch(err => {
    console.error('Failed to load Top3StudiesRoute:', err);
    throw err;
  })
);

const FavoritesRoute = lazy(() => 
  import('@/features/favorites/FavoritesRoute').then(module => ({ default: module.FavoritesRoute })).catch(err => {
    console.error('Failed to load FavoritesRoute:', err);
    throw err;
  })
);

const MyProfileRoute = lazy(() => 
  import('@/features/profile/MyProfileRoute').then(module => ({ default: module.MyProfileRoute })).catch(err => {
    console.error('Failed to load MyProfileRoute:', err);
    throw err;
  })
);

const SupportRoute = lazy(() => 
  import('@/features/support/SupportRoute').then(module => ({ default: module.SupportRoute })).catch(err => {
    console.error('Failed to load SupportRoute:', err);
    throw err;
  })
);

const BonussenRoute = lazy(() => 
  import('@/features/bonussen/BonussenRoute').then(module => ({ default: module.BonussenRoute })).catch(err => {
    console.error('Failed to load BonussenRoute:', err);
    throw err;
  })
);

const PaymentSuccessPage = lazy(() => 
  import('@/features/consumer/PaymentSuccessPage').catch(err => {
    console.error('Failed to load PaymentSuccessPage:', err);
    throw err;
  })
);

const CreateAccountPage = lazy(() => 
  import('@/features/consumer/CreateAccountPage').catch(err => {
    console.error('Failed to load CreateAccountPage:', err);
    throw err;
  })
);

// Wrapper for lazy-loaded components with error boundary and suspense
function LazyWrapper({ children }: { children: React.ReactNode }) {
  return (
    <AppErrorBoundary>
      <Suspense fallback={<RouteLoader />}>
        {children}
      </Suspense>
    </AppErrorBoundary>
  );
}

// Wrapper for public routes without layout
function PublicLayout({ children }: { children: React.ReactNode }) {
  return (
    <AppErrorBoundary>
      <Suspense fallback={<RouteLoader />}>
        {children}
      </Suspense>
    </AppErrorBoundary>
  );
}

// Error fallback component
function PageError() {
  return (
    <div className="text-center py-12">
      <h2 className="text-2xl font-bold text-destructive mb-2">Page Error</h2>
      <p className="text-muted-foreground">
        Something went wrong loading this page.
      </p>
    </div>
  );
}

const appRoutes: RouteObject[] = [
  // Protected routes with AppShell layout
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AppShell />
      </ProtectedRoute>
    ),
    errorElement: <PageError />,
    children: [
      // Home route (index route)
      {
        index: true,
        element: (
          <LazyWrapper>
            <HomeRoute />
          </LazyWrapper>
        ),
      },
      // Initial Onboarding
      {
        path: 'start',
        element: (
          <LazyWrapper>
            <StartPageRoute />
          </LazyWrapper>
        ),
      },
      // Compare
      {
        path: 'compare',
        element: (
          <LazyWrapper>
            <Compare />
          </LazyWrapper>
        ),
      },
      // Top-3 Studies
      {
        path: 'top-3',
        element: (
          <LazyWrapper>
            <Top3StudiesRoute />
          </LazyWrapper>
        ),
      },
      // Favorites
      {
        path: 'favorites',
        element: (
          <LazyWrapper>
            <FavoritesRoute />
          </LazyWrapper>
        ),
      },
      // Assessments
      {
        path: 'assessments/personality',
        element: (
          <LazyWrapper>
            <PersonalityRoute />
          </LazyWrapper>
        ),
      },
      {
        path: 'assessments/interests',
        element: (
          <LazyWrapper>
            <InterestsRoute />
          </LazyWrapper>
        ),
      },
      {
        path: 'assessments/talents',
        element: (
          <LazyWrapper>
            <TalentsRoute />
          </LazyWrapper>
        ),
      },
      // Preferences
      {
        path: 'preferences',
        element: (
          <LazyWrapper>
            <PreferencesRoute />
          </LazyWrapper>
        ),
      },
      // Support
      {
        path: 'support',
        element: (
          <LazyWrapper>
            <SupportRoute />
          </LazyWrapper>
        ),
      },
      // Bonussen (consumer only - access control in navigation)
      {
        path: 'bonussen',
        element: (
          <LazyWrapper>
            <BonussenRoute />
          </LazyWrapper>
        ),
      },
      // My Profile
      {
        path: 'my-profile',
        element: (
          <LazyWrapper>
            <MyProfileRoute />
          </LazyWrapper>
        ),
      },
    ],
  },
  
  // Redirects for old routes
  {
    path: '/dashboard',
    element: <Navigate to={routes.HOME} replace />,
  },
  {
    path: '/profile',
    element: <Navigate to={routes.HOME} replace />,
  },
  {
    path: '/settings/preferences',
    element: <Navigate to={routes.PREFERENCES} replace />,
  },
  
  // Auth routes (public)
  {
    path: routes.SIGNIN,
    element: (
      <PublicLayout>
        <SignInRoute />
      </PublicLayout>
    ),
    errorElement: <PageError />,
  },
  {
    path: routes.SIGNUP,
    element: (
      <PublicLayout>
        <SignUpRoute />
      </PublicLayout>
    ),
    errorElement: <PageError />,
  },
  {
    path: routes.AUTH_FORGOT_PASSWORD,
    element: (
      <PublicLayout>
        <ForgotPasswordRoute />
      </PublicLayout>
    ),
    errorElement: <PageError />,
  },
  {
    path: `${routes.AUTH_WELCOME}/*`,
    element: (
      <PublicLayout>
        <PasswordSetupRoute mode="invite" />
      </PublicLayout>
    ),
    errorElement: <PageError />,
  },
  {
    path: `${routes.AUTH_HANDLE_PASSWORD_RESET}/*`,
    element: (
      <PublicLayout>
        <PasswordSetupRoute mode="reset" />
      </PublicLayout>
    ),
    errorElement: <PageError />,
  },
  
  // Consumer routes (payment flow)
  {
    path: routes.CONSUMER.PAYMENT_SUCCESS,
    element: (
      <PublicLayout>
        <PaymentSuccessPage />
      </PublicLayout>
    ),
    errorElement: <PageError />,
  },
  {
    path: routes.CONSUMER.CREATE_ACCOUNT,
    element: (
      <PublicLayout>
        <CreateAccountPage />
      </PublicLayout>
    ),
    errorElement: <PageError />,
  },
  
  // Catch-all route for unknown paths - redirect to home
  // ProtectedRoute will handle auth redirects from there
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
];

export const router = createBrowserRouter(appRoutes);